import { useRef, useEffect, useState, useCallback } from 'react';
import { isTauri } from '../../utils/tauri';

const GOOGLE_MAPS_API_KEY = 'AIzaSyDzjxlTHg68Sl_Zr6FkOdyQGTJQDuvAWW4';
const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 }; // USA center

const MAP_STYLES: google.maps.MapTypeStyle[] = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
];

interface UseMapInitOptions {
  center?: { lat: number; lng: number };
  zoom?: number;
  height?: string;
  mapTypeControl?: boolean;
  streetViewControl?: boolean;
  geocodeAddress?: string;
  onMapReady?: (map: google.maps.Map) => void;
  onZoomChange?: (zoom: number) => void;
}

/**
 * Loads the Google Maps script (once), creates a map instance on the given
 * container element, and optionally geocodes an address for centering.
 */
export function useMapInit(options: UseMapInitOptions) {
  const {
    center,
    zoom = 4,
    mapTypeControl = true,
    streetViewControl = false,
    geocodeAddress,
    onMapReady,
    onZoomChange,
  } = options;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const scriptLoadedRef = useRef(false);

  // Load Google Maps script
  const ensureScript = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (window.google?.maps) {
        resolve();
        return;
      }
      if (scriptLoadedRef.current) {
        // Script is loading – poll until ready
        const interval = setInterval(() => {
          if (window.google?.maps) {
            clearInterval(interval);
            resolve();
          }
        }, 100);
        return;
      }
      scriptLoadedRef.current = true;

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=marker`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google Maps'));
      document.head.appendChild(script);
    });
  }, []);

  // Create / update map
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await ensureScript();
        if (cancelled || !containerRef.current) return;

        let mapCenter = center ?? DEFAULT_CENTER;
        let mapZoom = zoom;

        // Geocode address if provided
        if (geocodeAddress) {
          try {
            const geocoder = new window.google.maps.Geocoder();
            const result = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
              geocoder.geocode({ address: geocodeAddress }, (results, status) => {
                if (status === 'OK' && results?.[0]) resolve(results);
                else reject(new Error(`Geocoding failed: ${status}`));
              });
            });
            const location = result[0].geometry.location;
            mapCenter = { lat: location.lat(), lng: location.lng() };
            mapZoom = zoom || 15;
          } catch {
            // Fallback to default center if geocoding fails
          }
        }

        if (!mapRef.current) {
          const newMap = new window.google.maps.Map(containerRef.current, {
            zoom: mapZoom,
            center: mapCenter,
            mapTypeControl,
            mapTypeControlOptions: {
              style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
              mapTypeIds: ['roadmap', 'satellite', 'terrain'],
            },
            streetViewControl,
            fullscreenControl: true,
            zoomControl: true,
            clickableIcons: !isTauri(), // desktop only: disable Google's built-in place popups (they open google.com/maps in a new tab — dead on desktop). Web keeps them.
            styles: MAP_STYLES,
          });

          mapRef.current = newMap;

          if (onZoomChange) {
            newMap.addListener('zoom_changed', () => {
              onZoomChange(newMap.getZoom()!);
            });
          }

          onMapReady?.(newMap);
        } else {
          // Map already exists – just update center/zoom
          mapRef.current.setCenter(mapCenter);
          mapRef.current.setZoom(mapZoom);
        }

        setLoading(false);
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message || 'Map initialization failed');
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geocodeAddress, center?.lat, center?.lng]);

  return { containerRef, map: mapRef.current, loading, error };
}
